import { useRef, useState } from 'react';
import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';

import {
  Attachment,
  AttachmentAction,
  AttachmentActions,
  AttachmentContent,
  AttachmentDescription,
  AttachmentMedia,
  AttachmentTitle,
  AttachmentTrigger,
} from '@/components/ui/attachment';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { useUploadFile } from '@/hooks/use-upload';
import { cn } from '@/lib/utils';
import type { CommonFormFieldProps } from './form-input';

type UploaderStatus = 'idle' | 'uploading' | 'error';

interface FormUploaderProps<T extends FieldValues = FieldValues>
  extends CommonFormFieldProps<T> {
  /** 'avatar' mostra um preview circular pequeno; 'file' mostra um card com nome do arquivo. */
  variant?: 'file' | 'avatar';
  /** Valor do atributo `accept` do input — ex.: "image/*", "application/pdf,image/*". */
  accept?: string;
  /** Mostra um preview circular grande da imagem carregada, acima do card de arquivo. */
  avatar?: boolean;
  disabled?: boolean;
}

export function FormUploader<T extends FieldValues>({
  name,
  control,
  label,
  variant = 'file',
  accept,
  avatar,
  required,
  disabled,
  className,
}: FormUploaderProps<T>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<UploaderStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { mutate: upload } = useUploadFile();

  return (
    <Controller
      name={name as FieldPath<T>}
      control={control}
      render={({ field, fieldState }) => {
        const value = field.value as string | undefined;

        const handleFiles = (files: FileList | null) => {
          const file = files?.[0];
          if (!file) return;

          setStatus('uploading');
          setErrorMessage(null);

          upload(file, {
            onSuccess: (uploaded) => {
              field.onChange(uploaded.path);
              setStatus('idle');
            },
            onError: (error: any) => {
              setStatus('error');
              setErrorMessage(error?.message ?? 'Erro ao enviar o arquivo.');
            },
          });
        };

        const isDisabled = disabled || field.disabled;
        const openPicker = () => inputRef.current?.click();

        const handleRemove = () => {
          field.onChange(undefined);
          setStatus('idle');
          setErrorMessage(null);
          if (inputRef.current) inputRef.current.value = '';
        };

        const attachmentState =
          status === 'uploading'
            ? 'uploading'
            : status === 'error'
              ? 'error'
              : value
                ? 'done'
                : 'idle';
        const isImage =
          variant === 'avatar' || /\.(png|jpe?g|gif|webp|svg)$/i.test(value ?? '');
        const fileName = value ? decodeURIComponent(value.split('/').pop() ?? '') : null;

        return (
          <Field
            className={cn('col-span-full', className)}
            data-invalid={fieldState.invalid}
          >
            <FieldLabel htmlFor={name}>
              {label} {required && <strong className="text-destructive">*</strong>}
            </FieldLabel>

            {avatar && (
              // O <Field> antigo força `[&>*]:w-full` nos filhos diretos — sem o `!`
              // o Avatar vira uma pílula esticada em vez de um círculo de 96px.
              <button
                type="button"
                aria-label={`Escolher ${label.toLowerCase()}`}
                disabled={isDisabled}
                onClick={openPicker}
                className="w-24! shrink-0 cursor-pointer self-start rounded-full disabled:cursor-not-allowed"
              >
                <Avatar className="size-24! w-24! shrink-0">
                  <AvatarImage
                    src={value}
                    alt={fileName ?? label}
                    className="object-cover"
                  />
                  <AvatarFallback>
                    <Icon name="user" className="size-8 text-muted-foreground" />
                  </AvatarFallback>
                </Avatar>
              </button>
            )}

            <input
              ref={inputRef}
              id={name}
              type="file"
              accept={accept}
              disabled={disabled || field.disabled}
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />

            <Attachment
              state={attachmentState}
              orientation={variant === 'avatar' ? 'vertical' : 'horizontal'}
              className={cn(
                variant === 'avatar' && 'w-24 mx-0',
                (disabled || field.disabled) && 'opacity-60',
              )}
            >
              {/* Cobre o card inteiro: clicar em qualquer lugar abre o seletor. As ações (z-20) ficam por cima. */}
              {!isDisabled && (
                <AttachmentTrigger
                  aria-label={`Escolher ${label.toLowerCase()}`}
                  onClick={openPicker}
                />
              )}

              <AttachmentMedia variant={isImage && value ? 'image' : 'icon'}>
                {isImage && value ? (
                  <img src={value} alt={fileName ?? label} />
                ) : (
                  <Icon name="upload" />
                )}
              </AttachmentMedia>

              <AttachmentContent>
                <AttachmentTitle>
                  {status === 'uploading'
                    ? 'Enviando...'
                    : (fileName ?? 'Nenhum arquivo selecionado')}
                </AttachmentTitle>
                {status === 'error' && (
                  <AttachmentDescription>{errorMessage}</AttachmentDescription>
                )}
              </AttachmentContent>

              {!disabled && !field.disabled && (
                <AttachmentActions>
                  {status === 'error' ? (
                    <AttachmentAction
                      type="button"
                      onClick={() => inputRef.current?.click()}
                    >
                      <Icon name="rotate-ccw" />
                    </AttachmentAction>
                  ) : value ? (
                    <AttachmentAction type="button" onClick={handleRemove}>
                      <Icon name="x" />
                    </AttachmentAction>
                  ) : (
                    <AttachmentAction
                      type="button"
                      onClick={() => inputRef.current?.click()}
                    >
                      <Icon name="upload" />
                    </AttachmentAction>
                  )}
                </AttachmentActions>
              )}
            </Attachment>

            {fieldState.error && <FieldError errors={[fieldState.error]} />}
          </Field>
        );
      }}
    />
  );
}
